import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';

export const GATE_KEY = 'district_site_gate_v2';
// Beta-tester tracking. Populated on successful login. Consumed by
// BetaActivityTracker to attach every page view / action to a tester.
export const BETA_TESTER_KEY  = 'district_beta_tester_id';
export const BETA_SESSION_KEY = 'district_beta_session_id';
export const BETA_LABEL_KEY   = 'district_beta_label';

// Soft Cloud palette (matches the rest of the app)
const TEXT = '#2D2B55';
const MUTED = '#8B8FA8';
const INDIGO = '#6C7AE0';
const CORAL = '#E07A6C';

export default function SiteGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(GATE_KEY) === 'unlocked';
    } catch {
      return false;
    }
  });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (unlocked) {
      try { localStorage.setItem(GATE_KEY, 'unlocked'); } catch { /* ignore */ }
    }
  }, [unlocked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/site-gate/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (!res.ok) {
        setError('Invalid credentials. Please try again.');
      } else {
        const data = await res.json();
        try {
          localStorage.setItem(BETA_TESTER_KEY,  data.testerId);
          localStorage.setItem(BETA_SESSION_KEY, data.sessionId);
          localStorage.setItem(BETA_LABEL_KEY,   data.label || '');
        } catch { /* localStorage unavailable — degrade silently */ }
        setUnlocked(true);
      }
    } catch (_err) {
      setError('Could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (unlocked) return children;

  return (
    <div
      data-testid="site-gate-screen"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'radial-gradient(circle at 20% 0%, #F5F4FB 0%, #EDEDF7 50%, #E7E7F2 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Helvetica Neue", sans-serif',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#FFFFFF',
          borderRadius: 16,
          padding: '40px 36px 32px',
          boxShadow: '0 12px 40px rgba(45, 43, 85, 0.10)',
          border: '1px solid #E8E8F0',
        }}
      >
        {/* Brand mark */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
          <img
            src="/district-logo.png"
            alt="District"
            data-testid="site-gate-logo"
            style={{ height: 44, width: 'auto', marginBottom: 10 }}
          />
          <div style={{ fontSize: 11, color: MUTED, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Restricted access
          </div>
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 600, color: TEXT, margin: '0 0 6px', letterSpacing: '-0.01em', textAlign: 'center' }}>
          Sign in to continue
        </h1>
        <p style={{ fontSize: 13, color: MUTED, margin: '0 0 28px', lineHeight: 1.5, textAlign: 'center' }}>
          This environment is private. Please enter your credentials to access the District platform.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <label style={{ display: 'block', marginBottom: 16 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: TEXT, marginBottom: 6 }}>
              Username
            </span>
            <input
              data-testid="site-gate-username-input"
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => { setUsername(e.target.value); if (error) setError(''); }}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '11px 14px',
                fontSize: 14,
                color: TEXT,
                background: '#F8F8FC',
                border: '1px solid #DCDCEA',
                borderRadius: 10,
                outline: 'none',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onFocus={(e) => { e.target.style.borderColor = INDIGO; e.target.style.background = '#FFFFFF'; }}
              onBlur={(e) => { e.target.style.borderColor = '#DCDCEA'; e.target.style.background = '#F8F8FC'; }}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 8 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: TEXT, marginBottom: 6 }}>
              Password
            </span>
            <div style={{ position: 'relative' }}>
              <input
                data-testid="site-gate-password-input"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '11px 44px 11px 14px',
                  fontSize: 14,
                  color: TEXT,
                  background: '#F8F8FC',
                  border: '1px solid #DCDCEA',
                  borderRadius: 10,
                  outline: 'none',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                onFocus={(e) => { e.target.style.borderColor = INDIGO; e.target.style.background = '#FFFFFF'; }}
                onBlur={(e) => { e.target.style.borderColor = '#DCDCEA'; e.target.style.background = '#F8F8FC'; }}
              />
              <button
                data-testid="site-gate-toggle-password"
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  border: 'none',
                  background: 'transparent',
                  color: MUTED,
                  cursor: 'pointer',
                  borderRadius: 6,
                }}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          {error && (
            <div
              data-testid="site-gate-error"
              role="alert"
              style={{
                marginTop: 12,
                padding: '10px 12px',
                fontSize: 12.5,
                color: CORAL,
                background: '#FDEFEC',
                border: '1px solid #F5D6CF',
                borderRadius: 8,
              }}
            >
              {error}
            </div>
          )}

          <button
            data-testid="site-gate-submit-btn"
            type="submit"
            disabled={submitting || !username || !password}
            style={{
              marginTop: 22,
              width: '100%',
              padding: '12px 16px',
              fontSize: 14,
              fontWeight: 600,
              color: '#FFFFFF',
              background: submitting || !username || !password
                ? '#B6BCEB'
                : `linear-gradient(135deg, ${INDIGO}, #8A95E8)`,
              border: 'none',
              borderRadius: 10,
              cursor: submitting || !username || !password ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 14px rgba(108, 122, 224, 0.28)',
              transition: 'transform 0.08s ease, box-shadow 0.15s ease, background 0.15s',
              letterSpacing: '0.01em',
            }}
            onMouseDown={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = 'translateY(1px)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {submitting ? 'Signing in\u2026' : 'Sign in'}
          </button>
        </form>

        <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid #EFEFF5', fontSize: 11, color: MUTED, textAlign: 'center', lineHeight: 1.6 }}>
          District &middot; John Boner Neighborhood Center
        </div>
      </div>
    </div>
  );
}
